import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BannerModule } from '@vg-constellation/angular-16/banner';
import { ButtonModule } from '@vg-constellation/angular-16/button';
import { CheckboxModule } from '@vg-constellation/angular-16/checkbox';
import { HintErrorModule } from '@vg-constellation/angular-16/hint-error';
import { IconModule } from '@vg-constellation/angular-16/icon';
import { InputModule } from '@vg-constellation/angular-16/input';
import { LinkModule } from '@vg-constellation/angular-16/link';
import { LabelModule } from '@vg-constellation/angular-16/label';
import { RadioModule } from '@vg-constellation/angular-16/radio';
import { SelectModule } from '@vg-constellation/angular-16/select';
import { TextareaModule } from '@vg-constellation/angular-16/textarea';
import { AccordionModule } from '@vg-constellation/angular-16/accordion';
import { DetailsModule } from '@vg-constellation/angular-16/details';
import { DropdownModule } from '@vg-constellation/angular-16/dropdown';
import { NavOverflowModule } from '@vg-constellation/angular-16/nav-overflow';
import { CardModule } from '@vg-constellation/angular-16/card';
import { PaginationModule } from '@vg-constellation/angular-16/pagination';
import { SearchModule } from '@vg-constellation/angular-16/search';
import { ModalDialogModule } from '@vg-constellation/angular-16/modal-dialog';
import { ToastrModule } from 'ngx-toastr';
import {
  RecaptchaModule,
  RecaptchaFormsModule,
  RECAPTCHA_LANGUAGE,
} from 'ng-recaptcha';
import { ModalModule } from 'ngx-bootstrap/modal';
import { IMaskModule } from 'angular-imask';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { HomeComponent } from './home/home.component';
import { OverviewComponent } from './overview/overview.component';
import { GlobalHeaderComponent } from './global-header/global-header.component';
import { HeaderNavComponent } from './global-header/components/header-nav/header-nav.component';
import { HeaderUtilityComponent } from './global-header/components/header-utility/header-utility.component';
import { MobileNavComponent } from './global-header/components/mobile-nav/mobile-nav.component';
import { NavDropdownComponent } from './global-header/components/nav-dropdown/nav-dropdown.component';
import { GlobalFooterComponent } from './global-footer/global-footer.component';
import { AboutUsComponent } from './about-us/about-us.component';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { TestimonialsComponent } from './testimonials/testimonials.component';
import { TermsComponent } from './global-footer/terms/terms.component';
import { PrivacyPolicyComponent } from './global-footer/privacy-policy/privacy-policy.component';
import { StudentPrivacyNoticeComponent } from './global-footer/student-privacy-notice/student-privacy-notice.component';
import { GradesFooterComponent } from './grades-footer/grades-footer.component';
import { Grades23Component } from './grades/grades2-3/grades2-3.component';
import { GradesK1Component } from './grades/gradesk-1/grades-k-1.component';
import { MaterialsComponent } from './materials/materials.component';
import { Grades78Component } from './grades/grades7-8/grades7-8.component';
import { Grades910Component } from './grades/grades9-10/grades9-10.component';
import { Grades1112Component } from './grades/grades11-12/grades11-12.component';
import { Grades45Component } from './grades/grades4-5/grades4-5.component';
import { Grades6Component } from './grades/grades6/grades6.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { CustomToastrComponent } from './shared/components/custom-toastr/custom-toastr.component';
import { MaterialsFormComponent } from './materials-form/materials-form.components';
import { WebinarSignUpComponent } from './webinar-sign-up/webinar-sign-up.component';
import { OrderMaterialsComponent } from './order-materials/order-materials.component';
import { ThankYouModalComponent } from './shared/components/thank-you-modal/thank-you-modal.component';
import { UpdatesComponent } from './updates/updates.component';
import { UpdatesModalComponent } from './updates/updates-modal/updates-modal.component';
import { HomeV2Component } from './home-v2/home-v2.component';
import { InvestComponent } from './invest/invest.component';
import { DigitalComponent } from './digital/digital.component';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { GradesMenuComponent } from './grades-menu/grades-menu.component';
import { DropdownComponent } from '@vg-constellation/angular-16/dropdown';
import { ChipModule, ChipComponent } from '@vg-constellation/angular-16/chip';
import { SpinnerModule } from '@vg-constellation/angular-16/spinner';
import {
  GraphicPatternComponent,
  GraphicPatternModule,
} from '@vg-constellation/angular-16/graphic-pattern';
import { ReplaceLastSpacePipe } from './shared/pipes/replace-last-space-pipe';
import { LangLinkPipe } from './shared/pipes/lang-link.pipe';
import { FocusInvalidInputDirective } from './shared/directives/focus-invalid-input.directive';
import { TabsModule } from '@vg-constellation/angular-16/tabs';
import { AccessibilityHeaderComponent } from "./shared/components/accessibility-header/accessibility-header.component";
import { ClassroomMaterialsComponent } from "./shared/components/classroom-materials/classroom-materials.component";
import { ModalFocusDirective } from "./shared/directives/modal-focus.directive";
import { VideoPlayerComponent } from "./shared/components/video-player/video-player.component";
import { ProductHowItWorksComponent } from './shared/components/product-subpage/product-how-it-works/product-how-it-works.component';
import { ProductIntroBannerComponent } from './shared/components/product-subpage/product-intro-banner/product-intro-banner.component';
import { ProductProgramBenefitsComponent } from './shared/components/product-subpage/product-program-benefits/product-program-benefits.component';
import { ProductTestimonialsComponent } from './shared/components/product-subpage/product-testimonials/product-testimonials.component';
import { HeroCardComponent } from './home-v2/partials/hero-card/hero-card.component';
import { LearningLibraryComponent } from './learning-library/learning-library.component';
import { LessonCardComponent } from './shared/components/lesson-card/lesson-card.component';
import { LearningContentComponent } from './learning-library/learning-content/learning-content.component';
import { GlobalBannerComponent } from './global-banner/global-banner.component';
import { LearningQuizCompletionComponent } from './learning-library/learning-content/learning-quiz/learning-quiz-completion.component';
import { LearningQuizQuestionsComponent } from './learning-library/learning-content/learning-quiz/learning-quiz-questions.component';
import { LearningQuizInitialComponent } from './learning-library/learning-content/learning-quiz/learning-quiz-initial.component';
import { LearningQuizFormComponent } from './learning-library/learning-content/learning-quiz/learning-quiz-form/learning-quiz-form.component';
import { NicComponent } from './nic/nic.component';
import { CommonFeatureCardComponent } from './nic/common-feature-card/common-feature-card.component';
import { DateFormatPipe } from './shared/pipes/date-format.pipe';

@NgModule({
  declarations: [
    AppComponent,
    AccessibilityHeaderComponent,
    CustomToastrComponent,
    HomeComponent,
    OverviewComponent,
    GlobalHeaderComponent,
    HeaderNavComponent,
    HeaderUtilityComponent,
    MobileNavComponent,
    NavDropdownComponent,
    GlobalFooterComponent,
    HeroCardComponent,
    GradesFooterComponent,
    AboutUsComponent,
    ContactUsComponent,
    WebinarSignUpComponent,
    TestimonialsComponent,
    TermsComponent,
    PrivacyPolicyComponent,
    StudentPrivacyNoticeComponent,
    GradesK1Component,
    Grades23Component,
    MaterialsComponent,
    MaterialsFormComponent,
    Grades78Component,
    Grades910Component,
    Grades1112Component,
    Grades45Component,
    Grades6Component,
    OrderMaterialsComponent,
    PageNotFoundComponent,
    ThankYouModalComponent,
    UpdatesComponent,
    GradesMenuComponent,
    UpdatesModalComponent,
    ReplaceLastSpacePipe,
    DateFormatPipe,
    LangLinkPipe,
    FocusInvalidInputDirective,
    ModalFocusDirective,
    VideoPlayerComponent,
    HomeV2Component,
    DigitalComponent,
    InvestComponent,
    ClassroomMaterialsComponent,
    ProductHowItWorksComponent,
    ProductIntroBannerComponent,
    ProductProgramBenefitsComponent,
    ProductTestimonialsComponent,
    LearningLibraryComponent,
    NicComponent,
    CommonFeatureCardComponent,
    LessonCardComponent,
    LearningContentComponent,
    LearningQuizInitialComponent,
    LearningQuizQuestionsComponent,
    LearningQuizCompletionComponent,
    LearningQuizFormComponent,
    GlobalBannerComponent
  ],
  imports: [
    AccordionModule,
    BannerModule,
    BrowserModule,
    BrowserAnimationsModule,
    ButtonModule,
    TabsModule,
    CheckboxModule,
    DetailsModule,
    DropdownModule,
    NavOverflowModule,
    CardModule,
    PaginationModule,
    DropdownComponent,
    HttpClientModule,
    HintErrorModule,
    IconModule,
    IMaskModule,
    InputModule,
    LabelModule,
    ReactiveFormsModule,
    RadioModule,
    SelectModule,
    SpinnerModule,
    TextareaModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    ChipModule,
    ChipComponent,
    GraphicPatternComponent,
    GraphicPatternModule,
    SearchModule,
    TranslateModule.forRoot({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
    }),
    ModalModule.forRoot(),
    ModalDialogModule.forRoot(),
    CommonModule,
    ToastrModule.forRoot({
      toastComponent: CustomToastrComponent,
      closeButton: true,
      positionClass: 'toast-top-right',
      maxOpened: 1,
      autoDismiss: true,
      timeOut: 5000,
    }),
    AppRoutingModule,
    LinkModule,
  ],
  providers: [
    {
      provide: RECAPTCHA_LANGUAGE,
      useFactory: (translateService: TranslateService) => {
        return translateService.getCurrentLang() || translateService.getFallbackLang() || 'en';
      },
      deps: [TranslateService],
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
